import detectEthereumProvider from '@metamask/detect-provider';
import { ethers } from 'ethers';
import { walletSessionService, WalletSessionResponse, WalletSessionStatusResponse } from './walletSessionService';
import apiClient from './api';

export interface WalletAuthorizationResponse {
  is_authorized: boolean;
  wallet_address: string;
  whitelist_enabled: boolean;
  message: string;
  suggestion?: string;
}

export interface WalletService {
  connectWallet(): Promise<string>;
  signMessage(message: string): Promise<string>;
  disconnect(): void;
  isConnected(): boolean;
  getAddress(): string | null;
  getProvider(): any;
  // New session-related methods
  authenticateWallet(): Promise<WalletSessionResponse>;
  isAuthenticated(): Promise<boolean>;
  getSessionStatus(): Promise<WalletSessionStatusResponse>;
  revokeSession(): Promise<void>;
  hasValidSession(): boolean;
  checkWalletAuthorization(walletAddress: string): Promise<WalletAuthorizationResponse>;
}

class MetaMaskWalletService implements WalletService {
  private provider: any = null;
  private address: string | null = null;

  async connectWallet(): Promise<string> {
    try {
      this.provider = await detectEthereumProvider({ mustBeMetaMask: true }); // Ensure it's MetaMask

      if (!this.provider) {
        throw new Error('MetaMask not detected. Please install MetaMask.');
      }

      const accounts = await this.provider.request({
        method: 'eth_requestAccounts',
      });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. Please unlock MetaMask or create an account.');
      }

      const account = accounts[0] as string;
      if (!account) {
        throw new Error('No account found in accounts array.');
      }
      const fetchedAddress = ethers.getAddress(account);
      this.address = fetchedAddress;
      if (this.address) {
        localStorage.setItem('wallet_address', this.address);
      }

      // Listen for account changes
      if (this.provider.on) {
        this.provider.on('accountsChanged', this.handleAccountsChanged);
        this.provider.on('disconnect', this.handleDisconnect); // Handle MetaMask disconnect event
      }

      return this.address;
    } catch (error) {
      console.error('Wallet connection error:', error);
      // Clean up if connection failed partially
      this.address = null;
      this.provider = null;
      localStorage.removeItem('wallet_address');
      throw error;
    }
  }

  async signMessage(message: string): Promise<string> {
    if (!this.provider || !this.address) {
      throw new Error('Wallet not connected. Cannot sign message.');
    }

    try {
      // Using ethers.js for signing if provider is a standard EIP-1193 provider
      if (this.provider.getSigner) {
          const signer = await this.provider.getSigner();
          const signature = await signer.signMessage(message);
          return signature;
      } else {
        // Fallback to direct request if getSigner is not available (less common)
        const signature = await this.provider.request({
            method: 'personal_sign',
            params: [message, this.address],
        });
        return signature;
      }
    } catch (error) {
      console.error('Message signing error:', error);
      throw error;
    }
  }

  /**
   * Authenticate wallet by creating a session with the backend
   */
  async authenticateWallet(): Promise<WalletSessionResponse> {
    if (!this.address) {
      throw new Error('Wallet not connected. Cannot authenticate.');
    }

    try {
      // Get the message to sign from backend
      const authMessage = await walletSessionService.getAuthMessage(this.address);
      
      // Sign the message with the wallet
      const signature = await this.signMessage(authMessage.message);
      
      // Create session with backend
      const sessionResponse = await walletSessionService.createSession({
        wallet_address: this.address,
        signed_message: authMessage.message,
        signature: signature,
      });

      console.log('Wallet authenticated successfully', {
        address: this.address,
        sessionToken: sessionResponse.session_token,
        expiresAt: sessionResponse.expires_at,
      });

      return sessionResponse;
    } catch (error) {
      console.error('Wallet authentication error:', error);
      throw error;
    }
  }

  /**
   * Check if wallet is authenticated with a valid session
   */
  async isAuthenticated(): Promise<boolean> {
    if (!this.address) {
      return false;
    }

    try {
      // First check if we have a stored session token
      if (walletSessionService.hasSessionToken()) {
        const sessionStatus = await walletSessionService.validateSession();
        return sessionStatus.is_authorized;
      }

      // If no token, check wallet authorization status
      const walletStatus = await walletSessionService.checkWalletAuthorization(this.address);
      return walletStatus.is_authorized;
    } catch (error) {
      console.error('Error checking authentication status:', error);
      return false;
    }
  }

  /**
   * Get current session status
   */
  async getSessionStatus(): Promise<WalletSessionStatusResponse> {
    if (!this.address) {
      return { is_authorized: false };
    }

    try {
      // Try to validate current session first
      if (walletSessionService.hasSessionToken()) {
        const sessionStatus = await walletSessionService.validateSession();
        if (sessionStatus.is_authorized) {
          return sessionStatus;
        }
      }

      // Fallback to checking wallet authorization
      return await walletSessionService.checkWalletAuthorization(this.address);
    } catch (error) {
      console.error('Error getting session status:', error);
      return { is_authorized: false };
    }
  }

  /**
   * Revoke the current session
   */
  async revokeSession(): Promise<void> {
    try {
      await walletSessionService.revokeSession();
    } catch (error) {
      console.error('Error revoking session:', error);
      throw error;
    }
  }

  /**
   * Check if we have a valid session token stored locally
   */
  hasValidSession(): boolean {
    return walletSessionService.hasSessionToken();
  }

  async checkWalletAuthorization(walletAddress: string): Promise<WalletAuthorizationResponse> {
    try {
      const response = await apiClient.get<WalletAuthorizationResponse>(
        `/wallet/auth/check-authorization/${walletAddress}`
      );
      return response.data;
    } catch (error) {
      console.error('Error checking wallet authorization:', error);
      throw new Error('Failed to check wallet authorization');
    }
  }

  disconnect(): void {
    if (this.provider && this.provider.removeListener) {
        this.provider.removeListener('accountsChanged', this.handleAccountsChanged);
        this.provider.removeListener('disconnect', this.handleDisconnect);
    }
    
    // Clear wallet session when disconnecting
    walletSessionService.clearSession();
    
    this.address = null;
    this.provider = null; // Clear provider instance as well
    localStorage.removeItem('wallet_address');
    // Potentially emit an event or use a callback if a more reactive disconnect is needed upstream
  }

  isConnected(): boolean {
    // More robust check: provider should also exist
    return !!this.address && !!this.provider;
  }

  getAddress(): string | null {
    // Attempt to retrieve from localStorage if internal state is null but was persisted
    // This helps in scenarios where the service instance is new but a session existed.
    // However, connectWallet should ideally be called to re-establish a live connection.
    return this.address || localStorage.getItem('wallet_address');
  }

  getProvider(): any {
    return this.provider;
  }

  private handleAccountsChanged = (accounts: string[]) => {
    if (accounts.length === 0) {
      // MetaMask is locked or the user has disconnected all accounts.
      this.disconnect();
    } else {
      const account = accounts[0] as string;
      if (!account) {
        this.disconnect();
        return;
      }
      const newAddress = ethers.getAddress(account);
      if (newAddress && this.address !== newAddress) {
        // Clear session when account changes
        walletSessionService.clearSession();
        
        this.address = newAddress;
        localStorage.setItem('wallet_address', this.address);
        // If there's a global event emitter or state management, notify about the change.
        // For now, components relying on useWallet will re-render due to context changes.
      }
    }
    // Need a way to propagate this change to the React context.
    // This is handled by the WalletProvider's effect hook listening to service's provider.
  };

  private handleDisconnect = () => {
    // Handle programmatic disconnect or EIP-1193 disconnect event
    this.disconnect();
    // Similar to accountsChanged, ensure React context updates.
  };
}

export const walletService = new MetaMaskWalletService(); 